import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useExchange } from '../contexts/ExchangeContext'

export interface HubData {
  exchangerPhaseOne?: {
    names: string
    homeAddress: string
    taxIdNumber: string
  }
  buyer?: {
    names: string
  }
  closingCompanySale?: {
    name: string
    address: string
    phone: string
    closerName: string
    closerEmail: string
  }
  relinquished?: {
    addressOrLegalDescription: string
  }
  identificationForm?: {
    exchangerNames: string
    relinquishedPropertyAddress: string
    idRule: '3-property' | '200-percent' | '95-percent' | ''
    identifiedProperties: string[]
  }
  exchangerPhaseTwo?: {
    names: string
    homeAddress: string
    taxIdNumber: string
  }
  seller?: {
    names: string
  }
  closingCompanyPurchase?: {
    name: string
    address: string
    phone: string
    closerName: string
    closerEmail: string
  }
  replacement?: {
    addressOrLegalDescription: string
  }
  disbursementForm?: {
    exchangerNames: string
    amount: string
    wireRecipient: string
    wireRecipientAddress: string
    wireRecipientBankName: string
    wireRecipientBankABA: string
    wireRecipientBankAccount: string
  }
  documents?: Array<{
    name: string
    uploaded: boolean
    required: boolean
    category: string
    signed?: boolean
  }>
  lastSavedAt?: string
}

export const useHubData = (scope: 'initial' | 'exchange' | 'acquisition' | 'phase1' | 'all' = 'all', userId?: string | null) => {
  const { user } = useAuth()
  const { currentExchange } = useExchange()
  
  const getStorageKey = useCallback(() => {
    const exchangeId = currentExchange?.id || userId || user?.id || 'anon'
    return `sale_wizard_v1_${exchangeId}_${scope}`
  }, [currentExchange?.id, userId, user?.id, scope])

  const [data, setData] = useState<HubData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadData = useCallback(() => {
    try {
      const key = getStorageKey()
      const raw = localStorage.getItem(key)
      if (raw) {
        const parsed = JSON.parse(raw)
        setData(parsed)
      } else {
        setData(null)
      }
    } catch (error) {
      console.error('Failed to load hub data:', error)
      setData(null)
    } finally {
      setIsLoading(false)
    }
  }, [getStorageKey])

  const saveData = useCallback((newData: Partial<HubData>) => {
    try {
      const key = getStorageKey()
      const existing = data || {}
      const updated = {
        ...existing,
        ...newData,
        lastSavedAt: new Date().toISOString()
      }
      localStorage.setItem(key, JSON.stringify(updated))
      setData(updated)
      return true
    } catch (error) {
      console.error('Failed to save hub data:', error)
      return false
    }
  }, [data, getStorageKey])

  const getData = useCallback(<T extends keyof HubData>(key: T): HubData[T] | undefined => {
    return data?.[key]
  }, [data])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    const handleUpdate = () => {
      loadData()
    }
    window.addEventListener('wizardDataUpdated', handleUpdate)
    window.addEventListener('salesHubCompleted', handleUpdate)
    window.addEventListener('identificationHubCompleted', handleUpdate)
    return () => {
      window.removeEventListener('wizardDataUpdated', handleUpdate)
      window.removeEventListener('salesHubCompleted', handleUpdate)
      window.removeEventListener('identificationHubCompleted', handleUpdate)
    }
  }, [loadData])

  return {
    data,
    isLoading,
    loadData,
    saveData,
    getData,
    storageKey: getStorageKey()
  }
}

